/**
 * The number of successful transmissions of a device, for the battery-drain calculation
 * (tri-server#1591). Shown in the gateway's "Device Data". Failed transmissions are not shown:
 * the legacy frame carries no retry counter, so they are unknown, not zero.
 */

import { useAtom } from "jotai";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

import { authenticationAtom } from "../../State";
import { Button } from "@/components/ui/button";
import { useTransmissionCounts } from "./useTransmissionCounts";

interface Properties {
  /** Mongo `_id` or business id of the asset. */
  assetKey: string | undefined;
  className?: string;
}

export default function TransmissionCounts({ assetKey, className }: Properties) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const { data, loading, failed, retry } = useTransmissionCounts(authentication?.customer, assetKey);

  if (!assetKey) return null;

  const count = (value: number) => new Intl.NumberFormat(i18next.language).format(value);
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18next.language) : "–");

  const rows: Array<[string, number | undefined]> = [
    ["last7Days", data?.counts.last7Days],
    ["last30Days", data?.counts.last30Days],
    ["last90Days", data?.counts.last90Days],
    ["sinceActivation", data?.counts.sinceActivation],
  ];

  return (
    <section
      className={`min-w-0 rounded-xl border bg-card p-4 ${className ?? ""}`}
      aria-labelledby="transmission-counts-title"
      data-testid="transmission-counts"
    >
      <h3 id="transmission-counts-title" className="text-sm font-semibold">
        {t("assetPropertiesPanel.transmissions.title")}
      </h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {t("assetPropertiesPanel.transmissions.subtitle")}
      </p>

      {failed ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm" role="alert">
          <span>{t("assetPropertiesPanel.transmissions.loadFailed")}</span>
          <Button type="button" size="sm" variant="outline" onClick={retry}>
            {t("assetPropertiesPanel.transmissions.retry")}
          </Button>
        </div>
      ) : (
        <>
          <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-sm" aria-busy={loading}>
            {rows.map(([key, value]) => (
              <div key={key} className="contents" data-testid={`transmissions-${key}`}>
                <dt className="text-muted-foreground">
                  {t(`assetPropertiesPanel.transmissions.${key}`)}
                </dt>
                <dd className="text-right font-semibold tabular-nums">
                  {loading || value === undefined ? "…" : count(value)}
                </dd>
              </div>
            ))}
          </dl>
          {!loading && data ? (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="transmissions-last">
              {data.lastAt
                ? t("assetPropertiesPanel.transmissions.lastAt", { time: when(data.lastAt) })
                : t("assetPropertiesPanel.transmissions.none")}
            </p>
          ) : null}
          {!loading && !data ? (
            <p className="mt-3 text-xs text-muted-foreground">
              {t("assetPropertiesPanel.transmissions.none")}
            </p>
          ) : null}
        </>
      )}

    </section>
  );
}
